import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AccountBalanceOutlined as GovIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import { CommonChip } from "../../ui";

/**
 * Clean & Compact Government Tax Summary Cards
 */
const TaxStatsCards = ({ taxRate = 10, taxWallet = "", currency = "POL" }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyWallet = () => {
    if (!taxWallet) return;
    navigator.clipboard.writeText(taxWallet);
    setCopied(true);
    toast.success("Government tax wallet copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const truncatedWallet = taxWallet
    ? `${taxWallet.substring(0, 8)}...${taxWallet.substring(
        taxWallet.length - 6,
      )}`
    : "0x0000...0000";

  return (
    <Grid container spacing={2.5}>
      {/* 1. Government Tax Percentage */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: colors.dashboard.cardBg,
            borderRadius: borderRadiusPx.card,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            boxShadow: customShadows.cardSubtle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
              }}
            >
              <GovIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  color: colors.slate[500],
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Government Tax Rate
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.75rem",
                  color: colors.dashboard.value,
                  lineHeight: 1.1,
                  mt: 0.25,
                }}
              >
                {Number(taxRate || 0).toFixed(1)}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontSize: "0.75rem",
                  display: "block",
                  mt: 0.25,
                }}
              >
                Statutory withholding (Independent of platform)
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Grid>

      {/* 2. Government Tax Collection Wallet */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: colors.dashboard.cardBg,
            borderRadius: borderRadiusPx.card,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            boxShadow: customShadows.cardSubtle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: colors.purple[50],
                color: colors.purple[600],
              }}
            >
              <WalletIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  color: colors.slate[500],
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Wallet
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.25 }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.05rem",
                    color: colors.dashboard.value,
                    fontFamily: "monospace",
                  }}
                >
                  {truncatedWallet}
                </Typography>
                {taxWallet && (
                  <Tooltip title={copied ? "Copied!" : "Copy Address"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyWallet}
                      sx={{ p: 0.5 }}
                    >
                      <CopyIcon
                        sx={{ fontSize: 16, color: colors.slate[400] }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontSize: "0.75rem",
                  display: "block",
                  mt: 0.25,
                }}
              >
                
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
};

TaxStatsCards.propTypes = {
  taxRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  taxWallet: PropTypes.string,
  currency: PropTypes.string,
};

export default TaxStatsCards;
